/* DADDY — 3D interface room. Drag to spin, scroll/pinch to zoom. Falls back to the SVG room if WebGL is unavailable. */
import * as THREE from "./daddy-three.module.min.js";
import { OrbitControls } from "./daddy-OrbitControls.js";

const host = document.querySelector(".room");
const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;

function webglOK() {
  try { const c = document.createElement("canvas"); return !!(c.getContext("webgl2") || c.getContext("webgl")); }
  catch (e) { return false; }
}
if (host && webglOK()) init();

function tuftTexture(size, cells) {
  // black tufted leather: diamond pillows with chrome buttons
  const c = document.createElement("canvas"); c.width = c.height = size;
  const g = c.getContext("2d"), s = size / cells;
  g.fillStyle = "#050505"; g.fillRect(0, 0, size, size);
  for (let i = -1; i <= cells; i++) for (let j = -1; j <= cells * 2; j++) {
    const cx = i * s + (j % 2 ? s / 2 : 0), cy = j * s / 2;
    const r = g.createRadialGradient(cx - s * .08, cy - s * .12, s * .05, cx, cy, s * .62);
    r.addColorStop(0, "#4a4a4d"); r.addColorStop(.5, "#1c1c1e"); r.addColorStop(1, "#050505");
    g.fillStyle = r; g.beginPath();
    g.moveTo(cx, cy - s / 2); g.lineTo(cx + s / 2, cy); g.lineTo(cx, cy + s / 2); g.lineTo(cx - s / 2, cy); g.closePath(); g.fill();
  }
  for (let i = 0; i <= cells; i++) for (let j = 0; j <= cells * 2; j++) {
    const cx = i * s + (j % 2 ? 0 : s / 2), cy = j * s / 2;
    g.fillStyle = "#9a9a9a"; g.beginPath(); g.arc(cx, cy, s * .05, 0, 7); g.fill();
    g.fillStyle = "#eee"; g.beginPath(); g.arc(cx - s * .015, cy - s * .015, s * .018, 0, 7); g.fill();
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
  return t;
}

function init() {
  const canvas = document.createElement("canvas");
  canvas.className = "room-3d";
  canvas.setAttribute("aria-label", "3D view of Daddy's room. Drag to look around.");
  host.appendChild(canvas);
  const hint = document.createElement("div");
  hint.className = "room-hint"; hint.hidden = true; hint.setAttribute("aria-hidden", "true"); hint.textContent = "DRAG TO LOOK AROUND";
  host.appendChild(hint);

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.75));
  // software-only GPUs (no graphics chip): draw at lower resolution so the room still appears quickly
  let softGL = false;
  try { const gl0 = renderer.getContext(), dbg = gl0.getExtension("WEBGL_debug_renderer_info");
    softGL = !!dbg && /swiftshader|llvmpipe|software|basic render/i.test(gl0.getParameter(dbg.UNMASKED_RENDERER_WEBGL)); } catch (e) {}
  if (softGL && !/hq/.test(location.hash)) renderer.setPixelRatio(0.5);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#000");
  scene.fog = new THREE.Fog("#000", 8, 16);

  const camera = new THREE.PerspectiveCamera(62, 1, 0.05, 40);
  camera.position.set(1.7, 2.0, 3.0);
  const camHash = /cam=([-\d.]+),([-\d.]+),([-\d.]+)/.exec(location.hash); // preview-only camera override
  if (camHash) camera.position.set(+camHash[1], +camHash[2], +camHash[3]);

  const controls = new OrbitControls(camera, canvas);
  controls.target.set(0, 1.75, 0);
  controls.enableDamping = true; controls.dampingFactor = 0.08;
  controls.enablePan = false;
  controls.minDistance = 1.3; controls.maxDistance = 3.7;   // stays inside the 4.5 m octagon
  controls.minPolarAngle = Math.PI * 0.28; controls.maxPolarAngle = Math.PI * 0.6;
  controls.autoRotate = !reduce && !camHash; controls.autoRotateSpeed = 0.55;
  controls.rotateSpeed = 0.6;
  let resumeT;
  controls.addEventListener("start", () => { controls.autoRotate = false; hint.classList.add("gone"); clearTimeout(resumeT); });
  controls.addEventListener("end", () => { if (!reduce) resumeT = setTimeout(() => (controls.autoRotate = true), 6000); });

  /* WALK THE HALL: step through the door, walk to the end, turn around, walk back */
  const walkBtn = document.createElement("button");
  walkBtn.type = "button"; walkBtn.className = "walk-btn"; walkBtn.hidden = true; walkBtn.textContent = "[ WALK THE HALL ]";
  host.appendChild(walkBtn);
  let walk = null;
  const WSPD = /walkfast/.test(location.hash) ? 6 : 1;   // preview-only fast walk
  const EYE = 1.62, HALL_END = -10.5, DOOR_X = -4.5;
  const yawPitch = (from, to) => { const d = to.clone().sub(from).normalize(); return [Math.atan2(d.x, d.z), Math.asin(d.y)]; };
  const ease = x => x < .5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
  function startWalk() {
    if (walk) return;
    clearTimeout(resumeT); controls.autoRotate = false; controls.enabled = false; hint.classList.add("gone");
    const p0 = camera.position.clone(), [y0, q0] = yawPitch(p0, controls.target);
    let ya = -Math.PI / 2; while (ya - y0 > Math.PI) ya -= 2 * Math.PI; while (y0 - ya > Math.PI) ya += 2 * Math.PI;   // shortest turn toward the hall
    const A = new THREE.Vector3(-2.3, EYE, 0), B = new THREE.Vector3(HALL_END, EYE, 0), C = new THREE.Vector3(-2.1, EYE, 0);
    walk = { t: 0, seg: [
      { d: 2.2, p: [p0, A], y: [y0, ya], q: [q0, -.04] },
      { d: 7.0, p: [A, B], y: [ya, ya], q: [-.04, -.03], bob: 1 },
      { d: 1.4, p: [B, B], y: [ya, ya - Math.PI * .5], q: [-.03, -.02] },           // look at a cabin door
      { d: 1.6, p: [B, B], y: [ya - Math.PI * .5, ya - Math.PI], q: [-.02, -.05] }, // finish turning around
      { d: 7.0, p: [B, C], y: [ya - Math.PI, ya - Math.PI], q: [-.05, -.06], bob: 1 },
      { d: 2.2, p: [C, p0], y: [ya - Math.PI, y0 + (Math.round((ya - Math.PI - y0) / (2 * Math.PI)) * 2 * Math.PI)], q: [-.06, q0] },
    ], i: 0, dist: 0, moaned: false };
    walkBtn.disabled = true; walkBtn.textContent = "[ WALKING... ]";
  }
  const _v = new THREE.Vector3(), _l = new THREE.Vector3();
  function stepWalk(dt) {
    const w = walk, s = w.seg[w.i]; w.t += dt; host.dataset.walk = w.i + ':' + (w.t / s.d).toFixed(2);
    const k = Math.min(w.t / s.d, 1), e = s.bob ? k : ease(k);   // steady pace in the hall, eased at the ends
    _v.lerpVectors(s.p[0], s.p[1], e);
    const yaw = s.y[0] + (s.y[1] - s.y[0]) * ease(k), pit = s.q[0] + (s.q[1] - s.q[0]) * ease(k);
    if (s.bob && !reduce) { w.dist += dt; _v.y += Math.sin(w.dist * 11) * .022; _v.z += Math.sin(w.dist * 5.5) * .018; }
    camera.position.copy(_v);
    _l.set(Math.sin(yaw) * Math.cos(pit), Math.sin(pit), Math.cos(yaw) * Math.cos(pit)).add(_v); camera.lookAt(_l);
    if (w.i === 2 && !w.moaned && window.MotherSound) { w.moaned = true; window.MotherSound.alert(); }   // someone's busy in a cabin
    if (k >= 1) { w.i++; w.t = 0;
      if (w.i >= w.seg.length) { walk = null; camera.lookAt(controls.target); controls.enabled = true;
        walkBtn.disabled = false; walkBtn.textContent = "[ WALK THE HALL ]";
        if (!reduce) resumeT = setTimeout(() => (controls.autoRotate = true), 6000); } }
  }
  walkBtn.addEventListener("click", startWalk);
  // tapping the doorway also starts the walk
  const ray = new THREE.Raycaster(), ndc = new THREE.Vector2(); let downAt = null;
  canvas.addEventListener("pointerdown", e => { downAt = [e.clientX, e.clientY]; });
  canvas.addEventListener("pointerup", e => {
    if (!downAt || walk || Math.hypot(e.clientX - downAt[0], e.clientY - downAt[1]) > 6) return;
    const r = canvas.getBoundingClientRect(); ndc.set((e.clientX - r.left) / r.width * 2 - 1, -(e.clientY - r.top) / r.height * 2 + 1);
    ray.setFromCamera(ndc, camera); if (ray.intersectObjects(doorPick, false).length) startWalk();
  });

  const crtCv = document.createElement("canvas"); crtCv.width = 256; crtCv.height = 192;
  const crtG = crtCv.getContext("2d"), crtTex = new THREE.CanvasTexture(crtCv); crtTex.colorSpace = THREE.SRGBColorSpace;
  /* B+B logo as a green-phosphor ship-computer display: logo inside an octagon of circuit traces */
  const bbCv = document.createElement("canvas"); bbCv.width = 512; bbCv.height = 640;
  const bb = bbCv.getContext("2d"), bbTex = new THREE.CanvasTexture(bbCv); bbTex.colorSpace = THREE.SRGBColorSpace;
  const traces = [];
  { const cx = 256, cy = 320, Rr = 205;
    const inOct = (x, y) => { for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4 + Math.PI / 8; if ((x - cx) * Math.cos(a) + (y - cy) * Math.sin(a) > Rr * Math.cos(Math.PI / 8)) return false; } return true; };
    for (let n = 0; n < 260; n++) {                    // random right-angle/diagonal traces, kept inside the octagon
      let x = cx + (Math.random() - .5) * 2 * Rr, y = cy + (Math.random() - .5) * 2 * Rr; if (!inOct(x, y)) continue;
      const pts = [[x, y]]; let dir = (Math.random() * 8) | 0;
      for (let st = 0; st < 5; st++) { const len = 8 + Math.random() * 26, a = dir * Math.PI / 4; x += Math.cos(a) * len; y += Math.sin(a) * len; if (!inOct(x, y)) break; pts.push([x, y]); dir = (dir + (Math.random() < .5 ? 1 : 7)) % 8; }
      if (pts.length > 1) traces.push(pts);
    } }
  const bbMark = new Image(); let bbGreen = null;
  bbMark.onload = () => {                              // tint the logo phosphor green
    const c = document.createElement("canvas"); c.width = bbMark.width; c.height = bbMark.height; const x = c.getContext("2d");
    x.drawImage(bbMark, 0, 0); x.globalCompositeOperation = "source-in"; x.fillStyle = "#8dffb0"; x.fillRect(0, 0, c.width, c.height); bbGreen = c; paintBB(0);
  };
  bbMark.src = new URL("./daddy-bb-mark-red.png", import.meta.url).href;
  function paintBB(t) {
    bb.fillStyle = "#010a04"; bb.fillRect(0, 0, 512, 640);
    bb.lineCap = "round"; bb.shadowColor = "#5dff8a";
    traces.forEach((p, i) => {
      const lit = .45 + .55 * Math.max(0, Math.sin(t * 2.2 + i * .7));
      bb.strokeStyle = `rgba(93,255,138,${.35 + .5 * lit})`; bb.lineWidth = 2.2; bb.shadowBlur = 6 * lit;
      bb.beginPath(); bb.moveTo(p[0][0], p[0][1]); for (let k = 1; k < p.length; k++) bb.lineTo(p[k][0], p[k][1]); bb.stroke();
      bb.fillStyle = bb.strokeStyle; bb.beginPath(); bb.arc(p[p.length - 1][0], p[p.length - 1][1], 2.6, 0, 7); bb.fill();
    });
    if (bbGreen) {                                     // logo plate in the middle of the circuit brain
      bb.shadowBlur = 0; bb.fillStyle = "#010a04"; bb.beginPath();
      for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4 + Math.PI / 8; bb.lineTo(256 + Math.cos(a) * 112, 320 + Math.sin(a) * 112); } bb.closePath(); bb.fill();
      bb.strokeStyle = "#8dffb0"; bb.lineWidth = 3; bb.stroke();
      const h = 130, w = h * bbGreen.width / bbGreen.height; bb.shadowColor = "#5dff8a"; bb.shadowBlur = 18; bb.drawImage(bbGreen, 256 - w / 2, 320 - h / 2, w, h);
    }
    bb.shadowBlur = 0; bb.font = "26px VT323, monospace"; bb.fillStyle = "#8dffb0";
    bb.fillText("DADDY // B+B CORE", 24, 44); bb.fillText("INTERFACE " + (Math.floor(t * 2) % 2 ? "\u2588" : " "), 24, 612);
    bb.fillStyle = "rgba(0,0,0,.33)"; for (let y = 0; y < 640; y += 3) bb.fillRect(0, y, 512, 1);
    bbTex.needsUpdate = true;
  }
  paintBB(0);
  /* ---- doorway like the Nostromo's: stepped beveled frame opening onto a bathhouse hallway with private rooms both sides ---- */
  const DOOR = { w: 1.5, h: 2.2, c: .34, y: 0 };
  function doorPath(w, h, c, y0, asPath) {             // flat bottom, chamfered top corners
    const P = asPath === false ? new THREE.Shape() : new THREE.Path(), x = w / 2;
    P.moveTo(-x, y0); P.lineTo(x, y0); P.lineTo(x, y0 + h - c); P.lineTo(x - c, y0 + h); P.lineTo(-x + c, y0 + h); P.lineTo(-x, y0 + h - c); P.closePath();
    return P;
  }
  const doorPick = [];
  function buildDoor(g) {
    const blackGloss = new THREE.MeshPhysicalMaterial({ color: "#060606", roughness: .25, metalness: .2, clearcoat: .8, clearcoatRoughness: .2 });
    const layers = [[leatherWall, .24, .2], [blackGloss, .16, .26], [redTrim, .08, .32]];   // [material, outward growth, depth]
    layers.forEach(([mat, grow, depth], i) => {
      const inner = i === 0 ? .16 : i === 1 ? .08 : 0;
      const sh = doorPath(DOOR.w + grow * 2, DOOR.h + grow, DOOR.c + grow * .4, DOOR.y, false);
      sh.holes.push(doorPath(DOOR.w + inner * 2, DOOR.h + inner, DOOR.c + inner * .4, DOOR.y));
      const fm = new THREE.Mesh(new THREE.ExtrudeGeometry(sh, { depth, bevelEnabled: true, bevelThickness: .02, bevelSize: .015, bevelSegments: 1 }), mat); g.add(fm); doorPick.push(fm);
    });
    // hallway: black lacquer walls, red edge-lit floor, red ceiling bars, cabin doors on both sides
    const HL = 7, hw = DOOR.w / 2, top = DOOR.y + DOOR.h;
    const hall = new THREE.ExtrudeGeometry(doorPath(DOOR.w, DOOR.h, DOOR.c, DOOR.y, false), { depth: HL, bevelEnabled: false });
    const hallM = new THREE.Mesh(hall, [new THREE.MeshBasicMaterial({ visible: false }),
      new THREE.MeshStandardMaterial({ color: "#0b0b0b", roughness: .3, metalness: .5, side: THREE.BackSide })]);
    hallM.position.z = -HL; g.add(hallM); doorPick.push(hallM);
    const glowRed = new THREE.MeshBasicMaterial({ color: "#ff2238", toneMapped: false });
    [-1, 1].forEach(sd => { const st = new THREE.Mesh(new THREE.BoxGeometry(.03, .02, HL), glowRed); st.position.set(sd * (hw - .04), .012, -HL / 2); g.add(st); });  // floor edge lights
    for (let z = -.6; z > -HL; z -= .9) { const b = new THREE.Mesh(new THREE.BoxGeometry(DOOR.w - 2 * DOOR.c - .1, .02, .08), glowRed); b.position.set(0, top - .015, z); g.add(b); }
    const slab = new THREE.MeshStandardMaterial({ map: tuft, bumpMap: tuft, bumpScale: 2, roughness: .35, metalness: .15 });
    const numCv = n => { const c = document.createElement("canvas"); c.width = 128; c.height = 64; const x = c.getContext("2d");
      x.fillStyle = "#120003"; x.fillRect(0, 0, 128, 64); x.font = "bold 40px VT323, monospace"; x.fillStyle = "#ff3048"; x.textAlign = "center"; x.fillText(String(n).padStart(2, "0"), 64, 46);
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t; };
    let num = 1;
    for (let z = -1.1; z > -HL + .6; z -= 1.35) [-1, 1].forEach(sd => {
      const d = new THREE.Group(); d.position.set(sd * hw, 0, z); d.rotation.y = -sd * Math.PI / 2; g.add(d);   // local +z faces into the hallway
      const fr = new THREE.Mesh(new THREE.BoxGeometry(.82, 1.98, .04), redTrim); fr.position.set(0, .99, .005); d.add(fr);
      const ajar = (num % 3 === 2);
      if (ajar) {                                     // an open room: dark red glow inside, door swung in
        const inside = new THREE.Mesh(new THREE.PlaneGeometry(.7, 1.9), new THREE.MeshBasicMaterial({ color: "#3a0209", toneMapped: false })); inside.position.set(0, .95, .03); d.add(inside);
        const door = new THREE.Mesh(new THREE.BoxGeometry(.7, 1.9, .04), slab); door.position.set(-.33, .95, -.3); door.rotation.y = 1.1; d.add(door);
      } else { const door = new THREE.Mesh(new THREE.BoxGeometry(.7, 1.9, .05), slab); door.position.set(0, .95, .03); d.add(door); }
      const plate = new THREE.Mesh(new THREE.PlaneGeometry(.2, .1), new THREE.MeshBasicMaterial({ map: numCv(num++), toneMapped: false })); plate.position.set(0, 2.06, .03); d.add(plate);
    });
    const end = new THREE.Mesh(new THREE.PlaneGeometry(DOOR.w, DOOR.h), new THREE.MeshBasicMaterial({ color: "#1a0206" })); end.position.set(0, DOOR.h / 2, -HL + .02); g.add(end);
    const cl1 = new THREE.PointLight("#ff2a40", 2.2, 4, 1.8); cl1.position.set(0, top - .3, -1.4); g.add(cl1);
    const cl2 = new THREE.PointLight("#ff2a40", 2.2, 4, 1.8); cl2.position.set(0, top - .3, -4.4); g.add(cl2);
    const cl3 = new THREE.PointLight("#ff2a40", 1.8, 3.5, 1.8); cl3.position.set(0, top - .3, -6.4); g.add(cl3);
  }
  /* ---- room shell: octagonal Nostromo-style chamber in black tufted leather ---- */
  const R = 5, HGT = 4.6, HW = 3.55, A = 4.5, A2 = 1.55;   // wall height, wall apothem, inner ceiling apothem
  const T8 = Math.tan(Math.PI / 8), FW = 2 * A * T8;
  const tuft = tuftTexture(512, 4); tuft.repeat.set(2, 1);
  const leatherWall = new THREE.MeshStandardMaterial({ map: tuft, bumpMap: tuft, bumpScale: 3, roughness: 0.32, metalness: 0.15 });
  const shellMat = new THREE.MeshStandardMaterial({ color: "#070707", roughness: .55, metalness: .3, side: THREE.DoubleSide });
  const trimMat = new THREE.MeshStandardMaterial({ color: "#bdbdbd", roughness: .25, metalness: 1 });
  const redTrim = new THREE.MeshStandardMaterial({ color: "#c8102e", emissive: "#3a0008", roughness: .35, metalness: .3 });

  const floor = new THREE.Mesh(new THREE.CircleGeometry(R, 64),
    new THREE.MeshStandardMaterial({ color: "#0c0c0c", roughness: 0.38, metalness: 0.5 }));
  floor.rotation.x = -Math.PI / 2; scene.add(floor);
  const rug = new THREE.Mesh(new THREE.RingGeometry(1.2, 1.32, 8),
    new THREE.MeshBasicMaterial({ color: "#c8102e", transparent: true, opacity: .55 }));
  rug.rotation.x = -Math.PI / 2; rug.rotation.z = Math.PI / 8; rug.position.y = 0.005; scene.add(rug);

  const lampSpots = [];                          // world positions of every pin light
  const v3 = (x, y, z) => new THREE.Vector3(x, y, z);
  function bulbGrid(group, cx, cy, w, h, z, cols, rows) {
    group.updateMatrixWorld(true);
    for (let i = 0; i < cols; i++) for (let j = 0; j < rows; j++)
      lampSpots.push(group.localToWorld(v3(cx - w / 2 + w * (i + .5) / cols, cy - h / 2 + h * (j + .5) / rows, z)));
  }
  const modMats = [], lipMats = [], dummy = new THREE.Object3D();
  function module(group, cx, cy, w, h) {        // raised beveled leather block with a pin-light grid (instanced below)
    group.updateMatrixWorld(true);
    dummy.position.set(cx, cy, 0.045); dummy.scale.set(w, h, 1); dummy.updateMatrix();
    modMats.push(group.matrixWorld.clone().multiply(dummy.matrix));
    dummy.position.set(cx, cy - h / 2, .05); dummy.scale.set(w + .03, 1, 1); dummy.updateMatrix();
    lipMats.push(group.matrixWorld.clone().multiply(dummy.matrix));
    bulbGrid(group, cx, cy, w - .16, h - .14, 0.1, Math.round(w / .14), Math.round(h / .13));
  }
  const crtScreens = [];
  for (let k = 0; k < 8; k++) {
    const c = k * Math.PI / 4;
    const g = new THREE.Group(); g.position.set(Math.sin(c) * A, 0, Math.cos(c) * A); g.rotation.y = c + Math.PI; scene.add(g);
    const isDoor = k === 6;
    if (isDoor) {                                                      // wall with a doorway cut out
      const ws = new THREE.Shape(); ws.moveTo(-FW / 2 - .01, 0); ws.lineTo(FW / 2 + .01, 0); ws.lineTo(FW / 2 + .01, HW); ws.lineTo(-FW / 2 - .01, HW); ws.closePath();
      ws.holes.push(doorPath(DOOR.w, DOOR.h, DOOR.c, DOOR.y));
      g.add(new THREE.Mesh(new THREE.ShapeGeometry(ws), shellMat));
      buildDoor(g);
    } else { const back = new THREE.Mesh(new THREE.PlaneGeometry(FW + .02, HW), shellMat); back.position.y = HW / 2; g.add(back); }
    [0.32, HW - .04].forEach(y => {
      if (isDoor && y < 1) { [-1, 1].forEach(sd => { const w2 = FW / 2 - DOOR.w / 2 - .26; const t = new THREE.Mesh(new THREE.BoxGeometry(w2, .05, .14), trimMat); t.position.set(sd * (FW / 2 - w2 / 2), y, .07); g.add(t); }); return; }
      const t = new THREE.Mesh(new THREE.BoxGeometry(FW, .05, .14), trimMat); t.position.set(0, y, .07); g.add(t); });
    const cols = 3, cw = FW / cols, rows = [0.72, 1.42, 2.12, 2.82], rh = 0.64;
    for (let ci = 0; ci < cols; ci++) for (let ri = 0; ri < rows.length; ri++) {
      const cx = -FW / 2 + cw * (ci + .5), cy = rows[ri];
      if (k === 4 && ci === 1 && ri >= 1) continue;                    // octagonal hatch
      if (k === 6 && ci === 1 && ri <= 2) continue;                    // doorway
      if (k === 5 && ci === 1 && (ri === 1 || ri === 2)) {             // big B+B interface screen, like Mother's brain display
        if (ri === 2) continue;
        const sy = (rows[1] + rows[2]) / 2, sw = cw - .06, sh = rows[2] - rows[1] + rh;
        const bez = new THREE.Mesh(new THREE.BoxGeometry(sw, sh, .2), new THREE.MeshStandardMaterial({ color: "#141414", roughness: .4, metalness: .45 }));
        bez.position.set(cx, sy, .1); g.add(bez);
        const rim = new THREE.Mesh(new THREE.BoxGeometry(sw * .86, sh * .8, .02), trimMat); rim.position.set(cx, sy, .2); g.add(rim);
        const scr = new THREE.Mesh(new THREE.PlaneGeometry(sw * .8, sh * .74), new THREE.MeshBasicMaterial({ map: bbTex, toneMapped: false }));
        scr.position.set(cx, sy, .212); g.add(scr);
        const glowL = new THREE.PointLight("#5dff8a", 1.2, 2.2, 2); glowL.position.set(cx, sy, .6); g.add(glowL);
        continue;
      }
      if ((k === 2 || k === 3) && ci === 1 && ri === 1) { // CRTs set into the panels
        const bez = new THREE.Mesh(new THREE.BoxGeometry(cw - .1, rh, .22), new THREE.MeshStandardMaterial({ color: "#151515", roughness: .4, metalness: .4 }));
        bez.position.set(cx, cy, .11); g.add(bez);
        const sc = new THREE.Mesh(new THREE.PlaneGeometry(cw * .55, rh * .62), new THREE.MeshBasicMaterial({ map: crtTex, toneMapped: false }));
        sc.position.set(cx, cy, .222); g.add(sc); crtScreens.push(sc);
        continue;
      }
      if (k === 6 && ri <= 2) {                                        // side panels squeezed beside the wide doorway
        const free = FW / 2 - (DOOR.w / 2 + .26), mw = free - .08; if (ci === 1 || mw < .2) continue;
        module(g, (ci === 0 ? -1 : 1) * (FW / 2 - free / 2), cy, mw, rh); continue;
      }
      module(g, cx, cy, cw - .1, rh);
    }
    if (k === 4) {                                                     // hatch: octagon frame + deep blue port
      const hy = 2.12;
      const frame = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.11, 8, 8), redTrim); frame.rotation.z = Math.PI / 8; frame.position.set(0, hy, .12); g.add(frame);
      const outer = new THREE.Mesh(new THREE.TorusGeometry(0.88, 0.05, 6, 8), trimMat); outer.rotation.z = Math.PI / 8; outer.position.set(0, hy, .1); g.add(outer);
      const port = new THREE.Mesh(new THREE.CircleGeometry(0.7, 8), new THREE.MeshBasicMaterial({ color: "#06202c", toneMapped: false }));
      port.rotation.z = Math.PI / 8; port.position.set(0, hy, .06); g.add(port);
      bulbGrid(g, 0, hy, .8, .8, .07, 6, 6);                            // lights of the room beyond the port
      const bar = new THREE.MeshBasicMaterial({ color: "#e8f6ff", toneMapped: false });  // white corridor light bars round the hatch
      for (let i = 0; i < 8; i++) {
        const a = i * Math.PI / 4, b = new THREE.Mesh(new THREE.BoxGeometry(.26, .05, .03), bar);
        b.position.set(Math.cos(a) * 1.0, hy + Math.sin(a) * 1.0, .12); b.rotation.z = a + Math.PI / 2; g.add(b);
      }
    }
  }
  [[modMats, new THREE.BoxGeometry(1, 1, 0.09), leatherWall], [lipMats, new THREE.BoxGeometry(1, .018, .1), trimMat]].forEach(([mats, geo, mat]) => {
    const im = new THREE.InstancedMesh(geo, mat, mats.length); mats.forEach((mx, i) => im.setMatrixAt(i, mx)); scene.add(im);
  });
  // faceted ceiling: 8 sloped leather facets stepping up to a lit octagonal crown
  const ceilTex = tuftTexture(512, 4); ceilTex.repeat.set(3, 1.4);
  const ceilMat = new THREE.MeshStandardMaterial({ map: ceilTex, bumpMap: ceilTex, bumpScale: 2, roughness: .4, metalness: .15, side: THREE.DoubleSide });
  const corner = (ap, ang) => { const r = ap / Math.cos(Math.PI / 8); return [Math.sin(ang) * r, Math.cos(ang) * r]; };
  for (let k = 0; k < 8; k++) {
    const c = k * Math.PI / 4, a0 = c - Math.PI / 8, a1 = c + Math.PI / 8;
    const [x0, z0] = corner(A, a0), [x1, z1] = corner(A, a1), [x2, z2] = corner(A2, a1), [x3, z3] = corner(A2, a0);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute([x0, HW, z0, x1, HW, z1, x2, HGT, z2, x0, HW, z0, x2, HGT, z2, x3, HGT, z3], 3));
    geo.setAttribute("uv", new THREE.Float32BufferAttribute([0, 0, 1, 0, .67, 1, 0, 0, .67, 1, .33, 1], 2));
    geo.computeVertexNormals(); scene.add(new THREE.Mesh(geo, ceilMat));
    const ridge = new THREE.Mesh(new THREE.CylinderGeometry(.025, .025, 1, 6), trimMat);   // chrome seam on each facet edge
    const pa = v3(x0, HW, z0), pb = v3(x3, HGT, z3); ridge.position.copy(pa).lerp(pb, .5);
    ridge.scale.y = pa.distanceTo(pb); ridge.quaternion.setFromUnitVectors(v3(0, 1, 0), pb.clone().sub(pa).normalize()); scene.add(ridge);
    for (let j = 0; j < 6; j++) {                                      // pin lights across the facet
      const v = (j + .6) / 6.4, ap = A + (A2 - A) * v, y = HW + (HGT - HW) * v - 0.03, half = ap * T8 * .82, n = Math.max(4, Math.round(half * 2 / .15));
      for (let i = 0; i < n; i++) {
        const u = -half + half * 2 * (i + .5) / n, off = .05;
        lampSpots.push(v3(Math.sin(c) * (ap - off) + Math.cos(c) * u, y, Math.cos(c) * (ap - off) - Math.sin(c) * u));
      }
    }
  }
  const crown = new THREE.Mesh(new THREE.CircleGeometry(A2 / Math.cos(Math.PI / 8), 8), ceilMat);
  crown.rotation.x = Math.PI / 2; crown.rotation.z = Math.PI / 8; crown.position.y = HGT; scene.add(crown);
  const crownTrim = new THREE.Mesh(new THREE.TorusGeometry(A2 / Math.cos(Math.PI / 8), .04, 6, 8), redTrim);
  crownTrim.rotation.x = Math.PI / 2; crownTrim.rotation.z = Math.PI / 8; crownTrim.position.y = HGT - .02; scene.add(crownTrim);
  const crownGlow = new THREE.Mesh(new THREE.CircleGeometry(.32, 8), new THREE.MeshBasicMaterial({ color: "#fff3d6", toneMapped: false }));
  crownGlow.rotation.x = Math.PI / 2; crownGlow.position.y = HGT - .01; scene.add(crownGlow);

  /* ---- pin lights: warm Nostromo amber with a few B+B reds ---- */
  const palette = ["#ffc77a", "#ffb85c", "#ffd59a", "#ffc070", "#ffcb85", "#ffb85c", "#ffd59a", "#ffc77a", "#ffbe6a", "#ffcb85", "#ffc070", "#ffd59a", "#ff3b3b"].map(c => new THREE.Color(c));
  const lampGeo = new THREE.OctahedronGeometry(0.022, 0); lampGeo.scale(1, 1.6, .6);   // oblong film-style bulbs
  const lampMat = new THREE.MeshBasicMaterial({ toneMapped: false });
  const lamps = new THREE.InstancedMesh(lampGeo, lampMat, lampSpots.length);
  const m = new THREE.Matrix4(), base = [], on = [], dim = new THREE.Color();
  lampSpots.forEach((p, i) => {
    m.makeTranslation(p.x, p.y, p.z); lamps.setMatrixAt(i, m);
    const c = palette[(Math.random() * palette.length) | 0];
    base.push(c); on.push(Math.random() > 0.3);
    lamps.setColorAt(i, on[i] ? c : dim.copy(c).multiplyScalar(0.12));
  });
  lamps.instanceColor.needsUpdate = true;
  scene.add(lamps);

  /* ---- sling: modeled on the red/black leather swing reference ---- */
  const HUBY = HGT - 0.35;
  const sling = new THREE.Group(); sling.position.set(0, HUBY, 0); scene.add(sling);   // pivot at ceiling height for a gentle sway
  const S = new THREE.Group(); S.position.set(0, -HUBY, 0); sling.add(S);              // build in room coordinates
  const chrome = new THREE.MeshStandardMaterial({ color: "#e0e0e0", roughness: .15, metalness: 1 });
  const leather = new THREE.MeshPhysicalMaterial({ color: "#0b0b0b", roughness: .42, metalness: .1, clearcoat: .5, clearcoatRoughness: .35, side: THREE.DoubleSide });
  const redLeather = new THREE.MeshPhysicalMaterial({ color: "#c4001c", roughness: .33, metalness: .05, clearcoat: .7, clearcoatRoughness: .25 });
  const redStitch = new THREE.MeshStandardMaterial({ color: "#d0102a", roughness: .4 });

  // seat panel follows a reclined curve: front edge low/forward, headrest end high/back
  const L = 1.75, P0 = new THREE.Vector3(0, 0.8, 0.85), NSTEP = 90;   // ~1.75 m x 0.86 m body: a 6 ft (1.83 m) guy reclines with head on the pillow
  const curve = [];
  { let p = P0.clone();
    for (let i = 0; i <= NSTEP; i++) {
      const u = i / NSTEP, th = THREE.MathUtils.degToRad(5 + 45 * (u * u * (3 - 2 * u)));
      const T = new THREE.Vector3(0, Math.sin(th), -Math.cos(th)), N = new THREE.Vector3(0, Math.cos(th), Math.sin(th));
      curve.push({ p: p.clone(), T, N });
      p.addScaledVector(T, L / NSTEP);
    } }
  function frame(v) {
    const f = Math.min(NSTEP, Math.max(0, v / L * NSTEP)), i = Math.floor(Math.min(f, NSTEP - 1)), t = f - i;
    const a = curve[i], b = curve[i + 1];
    return { p: a.p.clone().lerp(b.p, t), T: a.T.clone().lerp(b.T, t).normalize(), N: a.N.clone().lerp(b.N, t).normalize() };
  }
  function place(obj, v, x, d) {           // local x = right, y = surface normal, z = up the seat
    const f = frame(v);
    obj.position.copy(f.p).add(new THREE.Vector3(x, 0, 0)).addScaledVector(f.N, d);
    obj.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(new THREE.Vector3(1, 0, 0), f.N, f.T));
    return obj;
  }
  function onSeat(x, v, d = 0) { const f = frame(v); return f.p.clone().add(new THREE.Vector3(x, 0, 0)).addScaledVector(f.N, d); }

  // black shield-shaped panel
  // bending needs vertices along v: rebuild panel as a strip grid instead of the sparse extrusion
  const grid = new THREE.PlaneGeometry(1, 1, 16, 70), ga = grid.attributes.position;
  function halfW(v) { // interpolate outline half-width
    const pts = [[0, 0.43], [0.35, 0.42], [1.1, 0.37], [L, 0.26]];
    for (let k = 0; k < pts.length - 1; k++) if (v <= pts[k + 1][0]) { const t = (v - pts[k][0]) / (pts[k + 1][0] - pts[k][0]); return pts[k][1] + (pts[k + 1][1] - pts[k][1]) * t; }
    return 0.26;
  }
  for (let i = 0; i < ga.count; i++) {
    const v = (ga.getY(i) + 0.5) * L, x = ga.getX(i) * 2 * halfW(v), q = onSeat(x, v);
    ga.setXYZ(i, q.x, q.y, q.z);
  }
  grid.computeVertexNormals();
  const seat = new THREE.Mesh(grid, leather); S.add(seat);
  // red piping around the panel edge
  const edgePts = [];
  for (let v = 0; v <= L; v += 0.04) edgePts.push(onSeat(halfW(v), v, 0.006));
  for (let x = 0.26; x >= -0.26; x -= 0.065) edgePts.push(onSeat(x, L, 0.006));
  for (let v = L; v >= 0; v -= 0.04) edgePts.push(onSeat(-halfW(v), v, 0.006));
  for (let x = -0.43; x <= 0.43; x += 0.086) edgePts.push(onSeat(x, 0, 0.006));
  S.add(new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(edgePts, true), 220, 0.012, 6, true), redStitch));

  // big round red cushion with a black B+B medallion in the center
  // bend any cushion onto the seat curve: local x = across, y = height off the leather, z = along the seat
  function conform(geo, v0, d0) {
    const pa = geo.attributes.position;
    for (let i = 0; i < pa.count; i++) { const q = onSeat(pa.getX(i), v0 + pa.getZ(i), d0 + pa.getY(i)); pa.setXYZ(i, q.x, q.y, q.z); }
    geo.computeVertexNormals(); return geo;
  }
  const DISC_V = L / 2, DISC_R = 0.31;   // cushion centered on the seat, flat, inside the panel edge
  const discProfile = [[0, 0], [DISC_R, 0], [DISC_R + 0.022, 0.014], [DISC_R + 0.032, 0.042], [DISC_R + 0.022, 0.07], [DISC_R - 0.01, 0.088], [0.15, 0.097], [0, 0.099]].map(([r, h]) => new THREE.Vector2(r, h));
  S.add(new THREE.Mesh(conform(new THREE.LatheGeometry(discProfile, 64), DISC_V, 0.004), redLeather));
  { const pipe = new THREE.TorusGeometry(DISC_R + 0.03, 0.008, 6, 96); pipe.rotateX(Math.PI / 2); pipe.translate(0, 0.042, 0);
    S.add(new THREE.Mesh(conform(pipe, DISC_V, 0.004), redStitch)); }
  const logoCv = document.createElement("canvas"); logoCv.width = logoCv.height = 512;
  const lg = logoCv.getContext("2d");
  lg.fillStyle = "#0b0b0b"; lg.beginPath(); lg.arc(256, 256, 250, 0, 7); lg.fill();
  lg.strokeStyle = "#d6102a"; lg.lineWidth = 10; lg.beginPath(); lg.arc(256, 256, 236, 0, 7); lg.stroke();
  lg.setLineDash([14, 10]); lg.lineWidth = 4; lg.strokeStyle = "#8a0a1c"; lg.beginPath(); lg.arc(256, 256, 214, 0, 7); lg.stroke();
  const logoTex = new THREE.CanvasTexture(logoCv); logoTex.colorSpace = THREE.SRGBColorSpace; logoTex.anisotropy = 4;
  const mark = new Image();
  mark.onload = () => { const h = 300, w = h * mark.width / mark.height; lg.drawImage(mark, 256 - w / 2, 256 - h / 2, w, h); logoTex.needsUpdate = true; };
  mark.src = new URL("./daddy-bb-mark-red.png", import.meta.url).href;
  const medalGeo = new THREE.CircleGeometry(0.23, 64);
  { const pa = medalGeo.attributes.position; // circle lies in XY: x across, y along the seat
    for (let i = 0; i < pa.count; i++) { const q = onSeat(pa.getX(i), DISC_V + pa.getY(i), 0.004 + 0.101); pa.setXYZ(i, q.x, q.y, q.z); }
    medalGeo.computeVertexNormals(); }
  S.add(new THREE.Mesh(medalGeo, new THREE.MeshPhysicalMaterial({ map: logoTex, roughness: .35, clearcoat: .6, clearcoatRoughness: .3 })));

  // red headrest pillow and front bolster
  function pillow(r, len, sy, sz, v0) {
    const g = new THREE.CapsuleGeometry(r, len, 8, 20); g.rotateZ(Math.PI / 2); g.scale(1, sy, sz); g.translate(0, r * sy, 0);
    return new THREE.Mesh(conform(g, v0, 0.004), redLeather);
  }
  S.add(pillow(0.085, 0.28, 0.85, 1.45, L - 0.15));  // headrest
  S.add(pillow(0.075, 0.7, 0.9, 2.0, 0.16));        // front bolster

  // four-point suspension: four chains to four ceiling anchors in the control room
  const ANCH = { back: [0.42, -1.05], front: [0.55, 1.0] };  // [x half-spread, z]
  const plateGeo = new THREE.CylinderGeometry(0.11, 0.11, 0.03, 32), eyeGeo = new THREE.TorusGeometry(0.045, 0.012, 8, 20);
  const anchorGlow = new THREE.MeshBasicMaterial({ color: "#c8102e", toneMapped: false });
  function anchor(x, z) {
    const plate = new THREE.Mesh(plateGeo, chrome); plate.position.set(x, HGT - 0.015, z); scene.add(plate);
    const glow = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.012, 6, 40), anchorGlow); glow.rotation.x = Math.PI / 2; glow.position.set(x, HGT - 0.03, z); scene.add(glow);
    const eye = new THREE.Mesh(eyeGeo, chrome); eye.position.set(x, HGT - 0.08, z); scene.add(eye);
    return new THREE.Vector3(x, HGT - 0.12, z);
  }
  const linkGeo = new THREE.TorusGeometry(0.03, 0.007, 6, 12);
  function chain(a, b) {
    const n = Math.max(2, Math.floor(a.distanceTo(b) / 0.05));
    for (let k = 0; k < n; k++) {
      const link = new THREE.Mesh(linkGeo, chrome); link.position.lerpVectors(a, b, (k + 0.5) / n);
      link.lookAt(b); link.rotateY(Math.PI / 2); if (k % 2) link.rotateX(Math.PI / 2); S.add(link);
    }
  }
  // studded black straps with red edges, D-rings and red leg sleeves
  const studGeo = new THREE.SphereGeometry(0.0065, 6, 4);
  const ringGeo = new THREE.TorusGeometry(0.04, 0.008, 8, 20);
  function strap(a, b, opts = {}) {
    const len = a.distanceTo(b), g = new THREE.Group();
    g.position.copy(a).lerp(b, 0.5); g.lookAt(b); S.add(g);
    // keep the strap face toward the room center/front
    g.add(new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.008, len), leather));
    [-0.031, 0.031].forEach(x => { const e = new THREE.Mesh(new THREE.BoxGeometry(0.007, 0.012, len), redStitch); e.position.x = x; g.add(e); });
    const n = Math.floor(len / 0.07), studs = new THREE.InstancedMesh(studGeo, chrome, n * 4), m4 = new THREE.Matrix4();
    let c = 0;
    for (let k = 0; k < n; k++) for (const sx of [-0.015, 0.015]) for (const sy of [-0.005, 0.005]) {
      m4.makeTranslation(sx, sy, -len / 2 + (k + 0.5) * len / n); studs.setMatrixAt(c++, m4);
    }
    g.add(studs);
    (opts.rings || []).forEach(t => { const r = new THREE.Mesh(ringGeo, chrome); r.position.z = -len / 2 + t * len; r.rotation.y = Math.PI / 2; g.add(r); });
    if (opts.sleeve) {
      const sl = new THREE.Mesh(new THREE.CapsuleGeometry(0.045, opts.sleeve[1] * len, 4, 12), redLeather);
      sl.rotation.x = Math.PI / 2; sl.scale.set(1, 1, 0.7); sl.position.z = -len / 2 + opts.sleeve[0] * len; g.add(sl);
    }
    return g;
  }
  const topCorner = s => onSeat(s * 0.26, L), frontCorner = s => onSeat(s * 0.43, 0);
  const STRAP_TOP = 2.75;   // straps end in a ring; chain runs from the ring up to the ceiling anchor
  const toward = (a, c, y) => a.clone().lerp(c, (y - a.y) / (c.y - a.y));
  [-1, 1].forEach(s => {
    // back straps: headrest corners → back ceiling anchors
    const a1 = topCorner(s), c1 = anchor(s * ANCH.back[0], ANCH.back[1]), b1 = toward(a1, c1, STRAP_TOP);
    strap(a1, b1, { rings: [0.22, 0.97] }); chain(b1, c1);
    // front straps: front corners → front ceiling anchors, with red padded leg sleeve
    const a2 = frontCorner(s), c2 = anchor(s * ANCH.front[0], ANCH.front[1]), b2 = toward(a2, c2, STRAP_TOP);
    strap(a2, b2, { rings: [0.18, 0.5, 0.97], sleeve: [0.62, 0.22] }); chain(b2, c2);
    const r1 = new THREE.Mesh(ringGeo, chrome); r1.position.copy(a1); S.add(r1);
    const r2 = new THREE.Mesh(ringGeo, chrome); r2.position.copy(a2); S.add(r2);
    // side support strap: upper panel edge → outer strap
    const mid = a2.clone().lerp(b2, 0.5), from = onSeat(s * halfW(1.3), 1.3, 0.01);
    const sup = new THREE.CatmullRomCurve3([from, from.clone().lerp(mid, 0.5).add(new THREE.Vector3(s * 0.05, -0.06, 0.02)), mid]);
    S.add(new THREE.Mesh(new THREE.TubeGeometry(sup, 24, 0.014, 6), leather));
    // stirrup strap hanging from the bar end, rings at both ends
    const st = b2.clone().add(new THREE.Vector3(s * 0.05, -0.02, -0.03));   // stirrups clip to the front strap rings
    strap(st.clone().add(new THREE.Vector3(s * 0.06, -0.95, -0.05)), st, { rings: [0.02, 0.98] });
  });

  /* ---- console + CRT ---- */
  const con = new THREE.Group(); con.position.set(0, 0, 2.7); scene.add(con);
  const desk = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.8, 0.7), new THREE.MeshStandardMaterial({ color: "#0e0e0e", roughness: .35, metalness: .3 }));
  desk.position.y = 0.4; con.add(desk);
  const deskTop = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.04, 0.8), trimMat); deskTop.position.y = 0.82; con.add(deskTop);
  const keyMat = new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: .5 }), keyRed = new THREE.MeshStandardMaterial({ color: "#c8102e", emissive: "#5a0010", roughness: .4 });
  const keyGeo = new THREE.BoxGeometry(0.07, 0.025, 0.07);
  for (let r = 0; r < 4; r++) for (let k = 0; k < 14; k++) {
    const key = new THREE.Mesh(keyGeo, Math.random() < .12 ? keyRed : keyMat);
    key.position.set(-0.52 + k * 0.08, 0.855, 0.08 + r * 0.08); con.add(key);
  }
  const glyphs = "01<>/=+#:.XO";
  function paintCRT() {
    crtG.fillStyle = "#021006"; crtG.fillRect(0, 0, 256, 192);
    crtG.font = "18px VT323, monospace"; crtG.fillStyle = "#7dff9a"; crtG.shadowColor = "#7dff9a"; crtG.shadowBlur = 6;
    crtG.fillText("DADDY", 14, 24);
    for (let l = 0; l < 8; l++) { let s = ""; for (let i = 0; i < 22; i++) s += glyphs[(Math.random() * glyphs.length) | 0]; crtG.fillText(s, 14, 46 + l * 18); }
    crtG.fillStyle = "rgba(0,0,0,.35)"; for (let y = 0; y < 192; y += 3) crtG.fillRect(0, y, 256, 1);
    crtTex.needsUpdate = true;
  }
  paintCRT();
  const monitor = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.5, 0.45), new THREE.MeshStandardMaterial({ color: "#1a1a1a", roughness: .4, metalness: .3 }));
  monitor.position.set(0, 1.12, -0.12); con.add(monitor);
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.52, 0.39), new THREE.MeshBasicMaterial({ map: crtTex, toneMapped: false }));
  screen.position.set(0, 1.12, 0.106); con.add(screen);

  /* ---- lighting ---- */
  scene.add(new THREE.AmbientLight("#ffffff", 0.35));
  for (let i = 0; i < 3; i++) {
    const a = i / 3 * Math.PI * 2 + 0.5;
    const wl = new THREE.PointLight(i === 2 ? "#ff4a5e" : "#ffd9a8", 14, 8, 1.5);
    wl.position.set(Math.sin(a) * 3.4, 3.6, Math.cos(a) * 3.4); scene.add(wl);
  }
  const key = new THREE.SpotLight("#ffffff", 7, 9, Math.PI / 5, 0.6, 1.4);
  key.position.set(0, HGT - 0.2, 0.6); const aim = new THREE.Object3D(); aim.position.set(0, 1.2, 0.1); scene.add(aim); key.target = aim; scene.add(key);
  const red = new THREE.PointLight("#ff1e3c", 3, 5, 1.8); red.position.set(0, 0.12, 1.4); scene.add(red);
  const fill = new THREE.HemisphereLight("#ffe2b8", "#200008", 0.5); scene.add(fill);

  /* ---- steam: soft puffs rising from floor vents, pooling under the ceiling ---- */
  const puffCv = document.createElement("canvas"); puffCv.width = puffCv.height = 128;
  { const pg = puffCv.getContext("2d"), gr = pg.createRadialGradient(64, 64, 0, 64, 64, 64);
    gr.addColorStop(0, "rgba(255,244,228,1)"); gr.addColorStop(.4, "rgba(255,240,220,.45)"); gr.addColorStop(1, "rgba(255,240,220,0)");
    pg.fillStyle = gr; pg.fillRect(0, 0, 128, 128); }
  const puffTex = new THREE.CanvasTexture(puffCv);
  const NP = 70, steamPos = new Float32Array(NP * 3), steamV = [];
  const vents = [[-2.4, 1.2], [2.4, 1.2], [-2.0, -2.2], [2.0, -2.2], [0, -3.2], [-3.2, -0.6], [3.2, -0.6]];
  function respawn(i, y0) {
    const v = vents[(Math.random() * vents.length) | 0];
    steamPos[i * 3] = v[0] + (Math.random() - .5) * .5; steamPos[i * 3 + 1] = y0 ?? Math.random() * .3; steamPos[i * 3 + 2] = v[1] + (Math.random() - .5) * .5;
    steamV[i] = [(Math.random() - .5) * .08, .22 + Math.random() * .25, (Math.random() - .5) * .08];
  }
  for (let i = 0; i < NP; i++) respawn(i, Math.random() * HW);
  const steamGeo = new THREE.BufferGeometry(); steamGeo.setAttribute("position", new THREE.BufferAttribute(steamPos, 3));
  const steamMat = new THREE.PointsMaterial({ map: puffTex, size: 1.6, transparent: true, opacity: .16, depthWrite: false, color: "#ffe7cc", fog: true });
  const steam = new THREE.Points(steamGeo, steamMat); steam.renderOrder = 5; scene.add(steam);
  // floor vents lit from underneath: red glow showing through black grate bars, light pooling on the floor
  const ventGlow = new THREE.MeshBasicMaterial({ color: "#ff2238", toneMapped: false });
  const ventBar = new THREE.MeshStandardMaterial({ color: "#050505", roughness: .4, metalness: .6 });
  const ventRim = new THREE.MeshPhysicalMaterial({ color: "#c8102e", roughness: .3, metalness: .4, clearcoat: .6 });
  const poolCv = document.createElement("canvas"); poolCv.width = poolCv.height = 128;
  { const pg = poolCv.getContext("2d"), gr = pg.createRadialGradient(64, 64, 10, 64, 64, 64);
    gr.addColorStop(0, "rgba(255,40,60,.9)"); gr.addColorStop(1, "rgba(255,40,60,0)"); pg.fillStyle = gr; pg.fillRect(0, 0, 128, 128); }
  const poolMat = new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(poolCv), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: .55, toneMapped: false });
  const barGeo = new THREE.BoxGeometry(.5, .012, .014), ventGroups = [];
  vents.forEach(([x, z]) => {
    const v = new THREE.Group(); v.position.set(x, 0, z); v.rotation.y = Math.atan2(x, z); scene.add(v);
    const pool = new THREE.Mesh(new THREE.PlaneGeometry(1.5, 1.5), poolMat); pool.rotation.x = -Math.PI / 2; pool.position.y = .003; v.add(pool);
    const glow = new THREE.Mesh(new THREE.CircleGeometry(.26, 8), ventGlow); glow.rotation.x = -Math.PI / 2; glow.rotation.z = Math.PI / 8; glow.position.y = .01; v.add(glow);
    const core = new THREE.Mesh(new THREE.CircleGeometry(.16, 16), new THREE.MeshBasicMaterial({ color: "#ffb0b8", toneMapped: false })); core.rotation.x = -Math.PI / 2; core.position.y = .011; v.add(core);   // hot center
    const rim = new THREE.Mesh(new THREE.TorusGeometry(.28, .025, 4, 8), ventRim); rim.rotation.x = -Math.PI / 2; rim.rotation.z = Math.PI / 8; rim.position.y = .012; v.add(rim);
    for (let i = -2; i <= 2; i++) { const b = new THREE.Mesh(barGeo, ventBar); b.scale.x = Math.sqrt(Math.max(0, 1 - (i / 2.6) ** 2)); b.position.set(0, .016, i * .095); v.add(b); }
    ventGroups.push(glow);
  });
  function stepSteam(dt, t) {
    for (let i = 0; i < NP; i++) {
      const v = steamV[i];
      steamPos[i * 3] += (v[0] + Math.sin(t * .5 + i) * .05) * dt; steamPos[i * 3 + 1] += v[1] * dt; steamPos[i * 3 + 2] += (v[2] + Math.cos(t * .4 + i) * .05) * dt;
      if (steamPos[i * 3 + 1] > HGT - .3) { v[1] *= .97; if (v[1] < .03) respawn(i); }      // linger under the crown, then recycle
    }
    steamGeo.attributes.position.needsUpdate = true;
    ventGlow.color.setRGB(1, .13 + .04 * Math.sin(t * 3.1), .22); poolMat.opacity = .5 + .08 * Math.sin(t * 2.3);
  }

  /* ---- loop ---- */
  function resize() {
    const w = host.clientWidth, h = host.clientHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.fov = w < 640 ? 74 : 62;
    camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(host); resize();

  let visible = true;
  new IntersectionObserver(es => { visible = es[0].isIntersecting; }).observe(host);
  const clock = new THREE.Clock();
  let blinkAcc = 0, crtAcc = 0, bbAcc = 0, shown3d = false, frames = 0;
  renderer.setAnimationLoop(() => {
    if (!visible || document.hidden) return;
    const dt = Math.min(clock.getDelta(), 0.05), t = clock.elapsedTime;
    if (!reduce) {
      sling.rotation.z = Math.sin(t * 0.9) * 0.025;
      sling.rotation.x = Math.sin(t * 0.7) * 0.015;
      blinkAcc += dt; crtAcc += dt;
      if (blinkAcc > 0.09) {
        blinkAcc = 0;
        const n = Math.max(20, (lampSpots.length * 0.025) | 0);
        for (let k = 0; k < n; k++) {
          const i = (Math.random() * lampSpots.length) | 0;
          on[i] = !on[i];
          lamps.setColorAt(i, on[i] ? base[i] : dim.copy(base[i]).multiplyScalar(0.12));
        }
        lamps.instanceColor.needsUpdate = true;
      }
      if (crtAcc > 0.6) { crtAcc = 0; paintCRT(); }
      bbAcc += dt; if (bbAcc > 0.15) { bbAcc = 0; paintBB(t); }
      stepSteam(dt, t);
    }
    if (walk) stepWalk(dt * WSPD); else controls.update(dt);
    renderer.render(scene, camera);
    if (!shown3d && ++frames >= 1) {
      // reveal 3D once the frame really drew; only fall back if nothing ever draws (broken GPU path)
      const gl = renderer.getContext(), w = gl.drawingBufferWidth, h = gl.drawingBufferHeight, px = new Uint8Array(4);
      let lit = 0;
      for (let gx = 1; gx < 9 && !lit; gx++) for (let gy = 1; gy < 7; gy++) {
        gl.readPixels((w * gx / 9) | 0, (h * gy / 7) | 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
        if (px[0] + px[1] + px[2] > 12) { lit++; break; }
      }
      if (lit) { shown3d = true; host.classList.add("is-3d"); hint.hidden = false; walkBtn.hidden = false; }
      else if (frames > 60) { renderer.setAnimationLoop(null); canvas.remove(); hint.remove(); walkBtn.remove(); }
    }
  });
}
